"use client";

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Search,
  Filter,
  List,
  Edit,
  MoreHorizontal,
  TrendingUp,
  BarChart3,
  Trash2,
  Info,
  ChevronLeft,
  ChevronDown,
  Plus,
  Minus,
} from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/shadcn-components/ui/context-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn-components/ui/select";
import { Input } from "@/shadcn-components/ui/input";
import { Label } from "@/shadcn-components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shadcn-components/ui/card";
import { Separator } from "@/shadcn-components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shadcn-components/ui/dialog";
import { useSettingsStore } from "@/lib/settings";
import ColumnsScreen from "./ColumnsScreen";
import SymbolActionsDrawer from "./components/SymbolActionsDrawer";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client";
import { useQuotesWatchlistStore } from "@/lib/quotes-watchlist/store";
import { useSymbolsStore } from "@/vybe-trader-client/symbols/stores/useSymbolsStore";
import SymbolSelectionScreen from "./symbol-selection/SymbolSelectionScreen";
import SymbolEditScreen from "./symbol-edit/SymbolEditScreen";
import { useVybeTraderClient } from "@/vybe-trader-client/connection/hooks/useVybeTraderClient";
import OrderTypeContentMapper from "../trades-view/trade-order/components/OrderTypeContentMapper";
import TradeOrderScreen from "../trades-view/trade-order/TradeOrderScreen";
import { useChartStore3 } from "@/vybe-trader-client/chart/stores/useChartStore3";
import { useDesktopLayoutStore } from "@/lib/desktop-layout/store";
import SymbolDetailsDialog from "./components/SymbolDetailsDialog";
import SymbolStatisticsDialog from "./components/SymbolStatisticsDialog";
import { WatchlistTable } from "./watchlist/WatchlistTable";
import type { WatchlistContextAction } from "./watchlist/WatchlistTableRow";
import { ConnectionFreshnessBanner } from "./watchlist/ConnectionFreshnessBanner";
import { LiveSymbolIcon } from "@/components/common";
import { useConnectionFreshnessStore } from "@/vybe-trader-client";

interface Quote {
  symbol: string;
  bid: number;
  ask: number;
  dayPercent: number;
  points: number;
  isIndex?: boolean;
  low?: number;
  high?: number;
  time?: string;
  bidHigh?: number;
  bidLow?: number;
  askHigh?: number;
  askLow?: number;
  last?: number;
  lastHigh?: number;
  lastLow?: number;
  spread?: number;
  updateInterval?: number;
  /** Last-tick direction (+1 up / -1 down / 0 flat) — drives the price colour like the desktop grid. */
  bidDirection: number;
  askDirection: number;
  lastDirection: number;
}

/** Signed tick direction → price-text class (blue up, red down, neutral flat) — mirrors watchlistQuoteUtils. */
function tickClass(direction: number): string {
  if (direction > 0) return "text-blue-400";
  if (direction < 0) return "text-red-400";
  return "text-foreground/90";
}

interface QuotesScreenProps {
  onNavigateToChart?: (symbol: string) => void;
  onNavigateToTrade?: (symbol: string) => void;
  isDesktop?: boolean;
}

// Separate component for live quote display - this will re-render only when quote changes
function LiveQuoteRow({
  currency,
  columns,
  isDetailedView,
  isDesktop = false,
  onSymbolClick,
  onSymbolContextAction,
  formatPrice,
  formatPriceWithSuperscript,
  formatPercent,
  getColumnValue,
  getColumnWidth,
  getColumnColor,
  selectedSymbolFromChart,
}: {
  currency: string;
  columns: string[];
  isDetailedView: boolean;
  isDesktop?: boolean;
  onSymbolClick: (symbol: string) => void;
  onSymbolContextAction: (
    symbol: string,
    action: "trade" | "chart" | "details" | "statistics" | "delete"
  ) => void;
  formatPrice: (price: number, symbol: string) => string;
  formatPriceWithSuperscript: (price: number, symbol: string) => any;
  formatPercent: (percent: number) => string;
  getColumnValue: (quote: Quote, columnName: string) => string;
  getColumnWidth: (columnName: string) => string;
  getColumnColor: (quote: Quote, columnName: string) => string;
  selectedSymbolFromChart?: string | null;
}) {
  // Subscribe only to this specific symbol's data
  const entry = useSubscribedSymbolsStore((state) =>
    state.byCurrency.get(currency)
  );

  if (!entry) return null;

  const quote = entry.quote;
  const symbol = entry.symbol;
  const hasOpenBuy = entry.hasOpenBuy;
  const hasOpenSell = entry.hasOpenSell;

  // Calculate derived values
  const spread = quote ? quote.ask - quote.bid : 0;
  const points =
    quote && quote.last > 0
      ? Math.round((quote.bid - quote.last) * Math.pow(10, symbol.digits))
      : 0;
  const dayPercent =
    quote && quote.lastPrice > 0
      ? ((quote.bid - quote.lastPrice) / quote.lastPrice) * 100
      : 0;

  // Format time
  const time = quote
    ? new Date(quote.time).toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : "00:00:00";

  const quoteData: Quote = {
    symbol: symbol.currency,
    bid: quote?.bid || 0,
    ask: quote?.ask || 0,
    dayPercent,
    points,
    low: quote?.bid || 0,
    high: quote?.ask || 0,
    time,
    bidHigh: quote ? quote.bid * 1.001 : 0,
    bidLow: quote ? quote.bid * 0.999 : 0,
    askHigh: quote ? quote.ask * 1.001 : 0,
    askLow: quote ? quote.ask * 0.999 : 0,
    last: quote?.last || quote?.bid || 0,
    lastHigh: quote?.last ? quote.last * 1.001 : (quote?.bid || 0) * 1.001,
    lastLow: quote?.last ? quote.last * 0.999 : (quote?.bid || 0) * 0.999,
    spread,
    updateInterval: Math.floor(Math.random() * 20) + 5,
    // Tick direction from the store's previous-tick fields. The mobile row used to hard-code Ask
    // red and colour Bid by a day-% that never updates, so every price read red (SR Global 2026-09-08).
    bidDirection: quote && quote.prevBid && quote.prevBid > 0 ? Math.sign(quote.bid - quote.prevBid) : 0,
    askDirection: quote && quote.prevAsk && quote.prevAsk > 0 ? Math.sign(quote.ask - quote.prevAsk) : 0,
    lastDirection:
      quote && quote.prevLast && quote.prevLast > 0 && quote.last
        ? Math.sign(quote.last - quote.prevLast)
        : 0,
  };
  const bidCls = tickClass(quoteData.bidDirection);
  const askCls = tickClass(quoteData.askDirection);

  // Check if this symbol is selected (for desktop mode)
  const isSelected = isDesktop && selectedSymbolFromChart === quoteData.symbol;
  const buttonBaseClass = `w-full ${
    isDesktop ? "px-3 py-2" : "px-3 py-1.5"
  } hover:bg-accent transition-colors`;
  const buttonClass = isSelected
    ? `${buttonBaseClass} bg-accent/60`
    : buttonBaseClass;

  if (isDetailedView) {
    const bidParts = formatPriceWithSuperscript(
      quoteData.bid,
      quoteData.symbol
    );
    const askParts = formatPriceWithSuperscript(
      quoteData.ask,
      quoteData.symbol
    );

    const content = (
      <div className="border-b border-border">
        <button
          onClick={() => onSymbolClick(quoteData.symbol)}
          className={buttonClass}
        >
          <div className="flex justify-between items-start">
            {/* Left Column */}
            <div className="flex flex-col justify-between h-full">
              {/* Points and Percentage */}
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={`text-foreground ${
                    isDesktop ? "text-xs" : "text-xs"
                  }`}
                >
                  {quoteData.points >= 0 ? "+" : ""}
                  {quoteData.points}
                </span>
                <span
                  className={`${isDesktop ? "text-xs" : "text-xs"} ${
                    quoteData.dayPercent >= 0 ? "text-blue-400" : "text-red-400"
                  }`}
                >
                  {formatPercent(quoteData.dayPercent)}
                </span>
              </div>

              {/* Symbol aligned with prices */}
              <div className="flex-1 flex items-center">
                <span
                  className={`text-foreground ${
                    isDesktop ? "text-base" : "text-base"
                  } font-normal flex items-center gap-1.5`}
                >
                  <LiveSymbolIcon
                    name={symbol.currency}
                    group={symbol.groupName}
                    description={symbol.description}
                    size={18}
                  />
                  {(hasOpenBuy || hasOpenSell) && (
                    <div
                      className={`${
                        isDesktop ? "h-[10px] w-[2px]" : "h-[14px] w-[2px]"
                      } ${
                        hasOpenBuy && hasOpenSell
                          ? "bg-orange-500"
                          : hasOpenBuy
                          ? "bg-green-500"
                          : "bg-red-500"
                      }`}
                    ></div>
                  )}
                  {quoteData.symbol}
                </span>
              </div>

              {/* Time aligned with L: and H: */}
              <div
                className={`flex items-center gap-3 ${
                  isDesktop ? "text-[10px]" : "text-[10px]"
                } text-muted-foreground mt-1`}
              >
                <span>{quoteData.time}</span>
                <span>⟳</span>
                <span>{quoteData.updateInterval}</span>
              </div>
            </div>

            {/* Right Column - Prices */}
            <div className="flex flex-col items-end">
              {/* Spacer for alignment */}
              <div className={`${isDesktop ? "h-[16px]" : "h-[20px]"}`}></div>

              {/* Bid/Ask Prices */}
              <div className="flex items-center gap-3 mb-1">
                {/* Bid Price — coloured by last-tick direction (was day-% sign, effectively constant) */}
                <div className="flex items-baseline">
                  <span className={`text-sm font-light ${bidCls}`}>{bidParts.prefix}</span>
                  <span className={`text-sm font-light ${bidCls}`}>{bidParts.main}</span>
                  <span className={`text-[24px] leading-none font-light ${bidCls}`}>{bidParts.middle}</span>
                  <sup className={`text-sm font-light ${bidCls}`}>{bidParts.super}</sup>
                </div>

                {/* Ask Price — coloured by last-tick direction (was hard-coded red) */}
                <div className="flex items-baseline">
                  <span className={`text-sm font-light ${askCls}`}>{askParts.prefix}</span>
                  <span className={`text-sm font-light ${askCls}`}>{askParts.main}</span>
                  <span className={`${isDesktop ? "text-[24px]" : "text-[32px]"} leading-none font-light ${askCls}`}>{askParts.middle}</span>
                  <sup className={`text-sm font-light ${askCls}`}>{askParts.super}</sup>
                </div>
              </div>

              {/* Low/High */}
              <div
                className={`flex items-center gap-3 ${
                  isDesktop ? "text-[10px]" : "text-[10px]"
                } text-muted-foreground`}
              >
                <span>
                  L:{" "}
                  {formatPrice(
                    quoteData.low || quoteData.bid,
                    quoteData.symbol
                  )}
                </span>
                <span>
                  H:{" "}
                  {formatPrice(
                    quoteData.high || quoteData.ask,
                    quoteData.symbol
                  )}
                </span>
              </div>
            </div>
          </div>
        </button>
      </div>
    );

    if (isDesktop) {
      return (
        <ContextMenu>
          <ContextMenuTrigger asChild>{content}</ContextMenuTrigger>
          <ContextMenuContent>
            <ContextMenuItem
              onClick={() => onSymbolContextAction(quoteData.symbol, "trade")}
            >
              <TrendingUp className="mr-2 h-4 w-4" />
              Trade
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() => onSymbolContextAction(quoteData.symbol, "chart")}
            >
              <BarChart3 className="mr-2 h-4 w-4" />
              Chart
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() => onSymbolContextAction(quoteData.symbol, "details")}
            >
              <Info className="mr-2 h-4 w-4" />
              Details
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() =>
                onSymbolContextAction(quoteData.symbol, "statistics")
              }
            >
              <BarChart3 className="mr-2 h-4 w-4" />
              Statistics
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() => onSymbolContextAction(quoteData.symbol, "delete")}
              className="text-red-600"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
      );
    }

    return content;
  }

  // Simple View
  const simpleButtonBaseClass = `w-full min-w-fit flex items-center ${
    isDesktop ? "px-3 py-2" : "px-3 py-2"
  } ${
    isDesktop ? "text-xs" : "text-xs"
  } hover:bg-accent transition-colors border-b border-border`;
  const simpleButtonClass = isSelected
    ? `${simpleButtonBaseClass} bg-accent/60`
    : simpleButtonBaseClass;

  const simpleContent = (
    <div>
      <button
        onClick={() => onSymbolClick(quoteData.symbol)}
        className={simpleButtonClass}
      >
        <div
          className={`${
            isDesktop ? "w-24" : "w-24"
          } flex-shrink-0 text-left font-medium text-foreground flex items-center gap-1`}
        >
          <LiveSymbolIcon
            name={symbol.currency}
            group={symbol.groupName}
            description={symbol.description}
            size={14}
          />
          {(hasOpenBuy || hasOpenSell) && (
            <div
              className={`${
                isDesktop ? "h-[8px] w-[2px]" : "h-[10px] w-[2px]"
              } ${
                hasOpenBuy && hasOpenSell
                  ? "bg-orange-500"
                  : hasOpenBuy
                  ? "bg-green-500"
                  : "bg-red-500"
              }`}
            ></div>
          )}
          {quoteData.symbol}
        </div>
        {columns.map((column) => (
          <div
            key={column}
            className={`${getColumnWidth(
              column
            )} flex-shrink-0 text-right ${getColumnColor(quoteData, column)}`}
          >
            {getColumnValue(quoteData, column)}
          </div>
        ))}
      </button>
    </div>
  );

  if (isDesktop) {
    return (
      <ContextMenu>
        <ContextMenuTrigger asChild>{simpleContent}</ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem
            onClick={() => onSymbolContextAction(quoteData.symbol, "trade")}
          >
            <TrendingUp className="mr-2 h-4 w-4" />
            Trade
          </ContextMenuItem>
          <ContextMenuItem
            onClick={() => onSymbolContextAction(quoteData.symbol, "chart")}
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            Chart
          </ContextMenuItem>
          <ContextMenuItem
            onClick={() => onSymbolContextAction(quoteData.symbol, "details")}
          >
            <Info className="mr-2 h-4 w-4" />
            Details
          </ContextMenuItem>
          <ContextMenuItem
            onClick={() =>
              onSymbolContextAction(quoteData.symbol, "statistics")
            }
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            Statistics
          </ContextMenuItem>
          <ContextMenuItem
            onClick={() => onSymbolContextAction(quoteData.symbol, "delete")}
            className="text-red-600"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    );
  }

  return simpleContent;
}

export default function QuotesScreen({
  onNavigateToChart,
  onNavigateToTrade,
  isDesktop = false,
}: QuotesScreenProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isDetailedView, setIsDetailedView] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [showSymbolSelection, setShowSymbolSelection] = useState(false);
  const [showSymbolEdit, setShowSymbolEdit] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  const [showTradeDialog, setShowTradeDialog] = useState(false);
  const [tradeDialogSymbol, setTradeDialogSymbol] = useState<string>("");
  const [detailsCurrency, setDetailsCurrency] = useState<string | null>(null);
  const [statisticsCurrency, setStatisticsCurrency] = useState<string | null>(
    null
  );

  // Chart store for desktop mode (focused panel drives watchlist selection)
  const focusedChartPanelId = useDesktopLayoutStore(
    (s) => s.focusedChartPanelId,
  );
  const symbolsStore = useSymbolsStore();

  // Get selected symbol from chart store for desktop mode
  const selectedSymbolFromChart = useChartStore3((state) =>
    isDesktop
      ? state.slots[focusedChartPanelId].selectedSymbol?.currency ?? null
      : null,
  );

  // Get list of watchlist symbols
  const watchlist = useQuotesWatchlistStore((state) => state.watchlist);

  // Get columns from settings store
  const columns = useSettingsStore((state) => state.settings.columns);

  // Sleep/wake freshness gate (proposal 23): while the feed is stale/resyncing,
  // dim the frozen last-good quotes so the trader isn't shown live-looking prices.
  const isFeedStale = useConnectionFreshnessStore((s) => s.freshness !== "live");

  // Refs for synchronized scrolling
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);

  // Synchronize horizontal scroll between header and content
  const handleHeaderScroll = () => {
    if (headerScrollRef.current && contentScrollRef.current) {
      contentScrollRef.current.scrollLeft = headerScrollRef.current.scrollLeft;
    }
  };

  const handleContentScroll = () => {
    if (contentScrollRef.current && headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = contentScrollRef.current.scrollLeft;
    }
  };

  const filteredSymbols = useMemo(() => {
    if (!searchQuery) return watchlist.map((item) => item.symbolKey);
    return watchlist
      .filter((item) =>
        item.symbolKey.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .map((item) => item.symbolKey);
  }, [watchlist, searchQuery]);

  const formatPrice = useCallback((price: number, symbol: string) => {
    // Determine decimals based on the symbol
    let decimals = 5;
    if (symbol.includes("JPY")) {
      decimals = 3;
    } else if (symbol === "GOLD" || symbol.includes("GOLD")) {
      decimals = 2;
    }
    return price.toFixed(decimals);
  }, []);

  const formatPriceWithSuperscript = useCallback(
    (price: number, symbol: string) => {
      const formattedPrice = formatPrice(price, symbol);
      const parts = formattedPrice.split(".");
      if (parts.length === 2) {
        const integerPart = parts[0];
        const decimalPart = parts[1];

        // For detailed view, we need special formatting:
        // First part (including decimal point) smaller
        // Middle part bigger
        // Last digit as superscript
        if (decimalPart.length >= 2) {
          const lastTwoDigits = decimalPart.slice(-2);
          const remainingDigits = decimalPart.slice(0, -2);

          return {
            prefix: integerPart + ".",
            main: remainingDigits.slice(0, -1), // all but last digit of remaining
            middle: remainingDigits.slice(-1) + lastTwoDigits.slice(0, 1), // last digit of remaining + first of last two
            super: lastTwoDigits.slice(-1), // last digit
          };
        }
      }
      return {
        prefix: "",
        main: formattedPrice,
        middle: "",
        super: "",
      };
    },
    [formatPrice]
  );

  const formatPercent = useCallback((percent: number) => {
    const sign = percent >= 0 ? "" : "";
    return `${sign}${percent.toFixed(2)}%`;
  }, []);

  const getColumnValue = useCallback(
    (quote: Quote, columnName: string) => {
      switch (columnName) {
        case "Bid":
          return formatPrice(quote.bid, quote.symbol);
        case "Ask":
          return formatPrice(quote.ask, quote.symbol);
        case "Day %":
          return formatPercent(quote.dayPercent);
        case "Bid High":
          return formatPrice(quote.bidHigh || quote.bid * 1.001, quote.symbol);
        case "Bid Low":
          return formatPrice(quote.bidLow || quote.bid * 0.999, quote.symbol);
        case "Ask High":
          return formatPrice(quote.askHigh || quote.ask * 1.001, quote.symbol);
        case "Ask Low":
          return formatPrice(quote.askLow || quote.ask * 0.999, quote.symbol);
        case "Last":
          return formatPrice(quote.last || quote.bid, quote.symbol);
        case "Last High":
          return formatPrice(quote.lastHigh || quote.bid * 1.001, quote.symbol);
        case "Last Low":
          return formatPrice(quote.lastLow || quote.bid * 0.999, quote.symbol);
        case "Time":
          return quote.time || "00:00:00";
        case "Spread":
          const spread = Math.abs(quote.ask - quote.bid);
          return formatPrice(spread, quote.symbol);
        default:
          return "-";
      }
    },
    [formatPrice, formatPercent]
  );

  const getColumnWidth = useCallback((columnName: string) => {
    switch (columnName) {
      case "Symbol":
        return "w-20";
      case "Time":
        return "w-28";
      case "Day %":
        return "w-14";
      case "Spread":
        return "w-20";
      default:
        return "w-24";
    }
  }, []);

  const getColumnColor = useCallback((quote: Quote, columnName: string) => {
    switch (columnName) {
      // Bid / Ask / Last are coloured by LAST-TICK direction (blue up, red down, neutral flat),
      // the same rule as the desktop WatchlistTableRow. Ask is no longer hard-coded red.
      case "Bid":
      case "Bid High":
      case "Bid Low":
        return tickClass(quote.bidDirection);
      case "Ask":
      case "Ask High":
      case "Ask Low":
        return tickClass(quote.askDirection);
      case "Last":
      case "Last High":
      case "Last Low":
        return tickClass(quote.lastDirection);
      // Day % stays a session-sign signal.
      case "Day %":
        return quote.dayPercent >= 0 ? "text-blue-400" : "text-red-400";
      default:
        return "text-foreground";
    }
  }, []);

  const handleSymbolClick = (symbol: string) => {
    if (isDesktop) {
      const symbolObject = symbolsStore.getSymbolByCurrency(symbol);
      if (symbolObject) {
        useChartStore3
          .getState()
          .setSelectedSymbol(symbolObject, focusedChartPanelId);
      }
    } else {
      // Na mobile/tablet pokazujemy drawer
      setSelectedSymbol(symbol);
    }
  };

  const handleSymbolContextAction = async (
    symbol: string,
    action: WatchlistContextAction
  ) => {
    switch (action) {
      case "trade":
        // Otwieramy dialog na desktop, TradeOrderScreen na mobile
        if (isDesktop) {
          setTradeDialogSymbol(symbol);
          setShowTradeDialog(true);
        } else if (onNavigateToTrade) {
          onNavigateToTrade(symbol);
        }
        break;
      case "chart": {
        const symbolObject = symbolsStore.getSymbolByCurrency(symbol);
        if (symbolObject) {
          const panelId = isDesktop
            ? useDesktopLayoutStore.getState().focusedChartPanelId
            : 0;
          useChartStore3.getState().setSelectedSymbol(symbolObject, panelId);
        }
        if (!isDesktop && onNavigateToChart) {
          onNavigateToChart(symbol);
        }
        break;
      }
      case "details":
        setDetailsCurrency(symbol);
        break;
      case "statistics":
        setStatisticsCurrency(symbol);
        break;
      case "delete":
        // Get symbol object and remove from watchlist
        const quotesWatchlistStore = useQuotesWatchlistStore.getState();
        const symbolObject = symbolsStore.getSymbolByCurrency(symbol);
        if (symbolObject) {
          await quotesWatchlistStore.removeFromWatchlist(symbolObject);
        }
        break;
    }
  };

  const handleDeleteSymbol = async () => {
    if (selectedSymbol) {
      // Get symbol object and remove from watchlist
      const symbolsStore = useSymbolsStore.getState();
      const quotesWatchlistStore = useQuotesWatchlistStore.getState();

      const symbol = symbolsStore.getSymbolByCurrency(selectedSymbol);
      if (symbol) {
        await quotesWatchlistStore.removeFromWatchlist(symbol);
      }
      setSelectedSymbol(null);
    }
  };

  if (showColumns) {
    return (
      <ColumnsScreen
        onBack={() => setShowColumns(false)}
        isDesktop={isDesktop}
      />
    );
  }

  if (showSymbolSelection) {
    return (
      <SymbolSelectionScreen
        onBack={() => setShowSymbolSelection(false)}
        isDesktop={isDesktop}
      />
    );
  }

  if (showSymbolEdit) {
    return (
      <SymbolEditScreen
        onBack={() => setShowSymbolEdit(false)}
        selectedSymbols={watchlist.map((item) => item.symbolKey)}
        onSymbolsChange={(symbols) => {
          // Handle symbol changes
        }}
        isDesktop={isDesktop}
      />
    );
  }

  return (
    <div className="h-full bg-background text-foreground flex flex-col">
      <div
        className={`flex shrink-0 items-center justify-between border-b border-border bg-background ${
          isDesktop ? "h-8 px-2" : "h-11 px-3"
        }`}
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsDetailedView(!isDetailedView)}
          className={`${isDesktop ? "h-6 w-6" : "h-9 w-9"}`}
        >
          <List
            className={`${
              isDesktop ? "h-3 w-3" : "h-4 w-4"
            } text-muted-foreground`}
          />
        </Button>
        <h1
          className={`${
            isDesktop ? "text-xs" : "text-sm"
          } font-medium text-foreground`}
        >
          Quotes
        </h1>
        <Button
          variant="ghost"
          size="icon"
          onClick={() =>
            isDetailedView ? setShowSymbolEdit(true) : setShowColumns(true)
          }
          className={`${isDesktop ? "h-6 w-6" : "h-9 w-9"}`}
        >
          {isDetailedView ? (
            <Edit
              className={`${
                isDesktop ? "h-3 w-3" : "h-5 w-5"
              } text-muted-foreground`}
            />
          ) : (
            <Filter
              className={`${
                isDesktop ? "h-3 w-3" : "h-5 w-5"
              } text-muted-foreground`}
            />
          )}
        </Button>
      </div>

      <div
        className={`shrink-0 border-b border-border ${
          isDesktop ? "px-2 py-1.5" : "px-3 py-1.5"
        }`}
      >
        <Button
          variant="ghost"
          onClick={() => setShowSymbolSelection(true)}
          className={`h-auto w-full justify-start rounded-md bg-accent hover:bg-accent/80 ${
            isDesktop ? "h-7 gap-2 px-2 py-1" : "h-9 gap-2 px-2.5 py-0"
          }`}
        >
          <Search
            className={`${
              isDesktop ? "h-3 w-3" : "h-4 w-4"
            } text-muted-foreground`}
          />
          <span
            className={`text-muted-foreground ${
              isDesktop ? "text-[11px]" : "text-xs"
            }`}
          >
            enter symbol for search
          </span>
        </Button>
      </div>

      {!isDetailedView && watchlist.length > 0 && !isDesktop && (
        <div
          ref={headerScrollRef}
          onScroll={handleHeaderScroll}
          className="scrollbar-hide shrink-0 overflow-x-auto overflow-y-hidden border-b border-border"
        >
          <div className="flex min-w-fit px-3 py-1 text-[11px] font-medium text-muted-foreground">
            <div className="w-24 shrink-0">Symbol</div>
            {columns.map((column) => (
              <div
                key={column}
                className={`${getColumnWidth(column)} shrink-0 text-right`}
              >
                {column}
              </div>
            ))}
          </div>
        </div>
      )}

      {watchlist.length === 0 ? (
        <div className="flex flex-1 items-center justify-center text-muted-foreground">
          <div className="text-center">
            <p className={isDesktop ? "text-sm" : "text-lg mb-2"}>
              No symbols in watchlist
            </p>
            <p className={isDesktop ? "text-[11px]" : "text-sm"}>
              Add symbols to see live quotes
            </p>
          </div>
        </div>
      ) : (
      <>
      <ConnectionFreshnessBanner isDesktop={isDesktop} />
      <div
        className={`flex min-h-0 flex-1 flex-col overflow-hidden transition-opacity ${
          isFeedStale ? "opacity-[0.42] grayscale-[0.55]" : ""
        }`}
      >
        {isDetailedView ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            {filteredSymbols.map((currency) => (
              <LiveQuoteRow
                key={currency}
                currency={currency}
                columns={columns}
                isDetailedView={true}
                isDesktop={isDesktop}
                onSymbolClick={handleSymbolClick}
                onSymbolContextAction={handleSymbolContextAction}
                formatPrice={formatPrice}
                formatPriceWithSuperscript={formatPriceWithSuperscript}
                formatPercent={formatPercent}
                getColumnValue={getColumnValue}
                getColumnWidth={getColumnWidth}
                getColumnColor={getColumnColor}
                selectedSymbolFromChart={selectedSymbolFromChart}
              />
            ))}
          </div>
        ) : isDesktop ? (
          <WatchlistTable
            symbols={filteredSymbols}
            columns={columns}
            selectedSymbol={selectedSymbolFromChart}
            onSymbolClick={handleSymbolClick}
            onSymbolContextAction={handleSymbolContextAction}
          />
        ) : (
          <div
            ref={contentScrollRef}
            onScroll={handleContentScroll}
            className="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
          >
            {filteredSymbols.map((currency) => (
              <LiveQuoteRow
                key={currency}
                currency={currency}
                columns={columns}
                isDetailedView={false}
                isDesktop={false}
                onSymbolClick={handleSymbolClick}
                onSymbolContextAction={handleSymbolContextAction}
                formatPrice={formatPrice}
                formatPriceWithSuperscript={formatPriceWithSuperscript}
                formatPercent={formatPercent}
                getColumnValue={getColumnValue}
                getColumnWidth={getColumnWidth}
                getColumnColor={getColumnColor}
                selectedSymbolFromChart={selectedSymbolFromChart}
              />
            ))}
          </div>
        )}
      </div>
      </>
      )}

      {/* Symbol Actions Drawer */}
      <SymbolActionsDrawer
        isOpen={!!selectedSymbol}
        onClose={() => setSelectedSymbol(null)}
        symbol={selectedSymbol || ""}
        onTrade={() => {
          if (onNavigateToTrade) {
            onNavigateToTrade(selectedSymbol!);
          }
        }}
        onChart={() => {
          const sym = selectedSymbol!;
          const symbolObject = symbolsStore.getSymbolByCurrency(sym);
          if (symbolObject) {
            useChartStore3.getState().setSelectedSymbol(symbolObject, 0);
          }
          if (onNavigateToChart) {
            onNavigateToChart(sym);
          }
        }}
        onDetails={() => setDetailsCurrency(selectedSymbol!)}
        onStatistics={() => setStatisticsCurrency(selectedSymbol!)}
        onDelete={handleDeleteSymbol}
      />

      {/* Trade Dialog - tylko na desktop */}
      {isDesktop && (
        <Dialog open={showTradeDialog} onOpenChange={setShowTradeDialog}>
          <DialogContent className={`${isDesktop ? 'max-w-md max-h-[70vh]' : 'max-w-2xl max-h-[90vh]'} p-4`}>
            <DialogHeader className="pb-2">
              <DialogTitle className="text-lg">Trade Order</DialogTitle>
            </DialogHeader>
            <div className="max-h-[55vh] overflow-y-auto">
              <TradeOrderScreen
                symbol={tradeDialogSymbol}
                type="new"
                isDesktop={isDesktop}
                onBack={() => setShowTradeDialog(false)}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      <SymbolDetailsDialog
        open={!!detailsCurrency}
        onOpenChange={(o) => !o && setDetailsCurrency(null)}
        currency={detailsCurrency}
      />
      <SymbolStatisticsDialog
        open={!!statisticsCurrency}
        onOpenChange={(o) => !o && setStatisticsCurrency(null)}
        currency={statisticsCurrency}
      />
    </div>
  );
}
